// ============================================================
// ✅ ValidationsPage.jsx - Page de validation des demandes
// Mobile-first : Manager/Admin valide les demandes N+1/N+2
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { 
  Check, X, Clock, Calendar, User, ChevronLeft, ChevronRight
} from 'lucide-react';
import ValidationModal from './ValidationModal';

export default function ValidationsPage() {
  const { user } = useUser();

  // ==========================================================
  // 📊 ÉTATS
  // ==========================================================
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [actionType, setActionType] = useState(''); // 'Approuve' ou 'Rejete'

  const [stats, setStats] = useState({ EnAttente: 0, Validees: 0, Refusees: 0 });

  // ==========================================================
  // 🔄 CHARGEMENT
  // ==========================================================
  useEffect(() => {
    if (user?.id) {
      fetchDemandes();
      fetchStats();
    }
  }, [user]);

  const fetchDemandes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/demandes/avalider/${user.id}`);
      setDemandes(res.data.demandes || []);
    } catch (err) {
      console.error('Erreur chargement validations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`/demandes/validations/stats/${user.id}`);
      setStats(res.data.stats || { EnAttente: 0, Validees: 0, Refusees: 0 });
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  // ==========================================================
  // 🖱️ HANDLERS
  // ==========================================================
  const handleAction = (demande, action) => {
    setSelectedDemande(demande);
    setActionType(action);
    setShowValidationModal(true);
  };

  // ✅ Rafraîchir après validation
  const handleValidationSuccess = () => {
    fetchDemandes();
    fetchStats();
  };

  // ==========================================================
  // 📊 PAGINATION
  // ==========================================================
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = demandes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(demandes.length / itemsPerPage);

  // ==========================================================
  // 🎨 RENDU
  // ==========================================================
  return (
    <div className="p-3 md:p-5 bg-gray-50 min-h-screen">

      {/* En-tête */}
      <div className="mb-4">
        <h2 className="text-base md:text-lg font-bold text-gray-800">✅ Validations</h2>
        <p className="text-[11px] text-gray-400">Demandes en attente de votre validation</p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-2xl font-bold text-amber-600">{stats.EnAttente || 0}</p>
          <p className="text-[10px] text-amber-500">En attente</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
          <p className="text-2xl font-bold text-green-600">{stats.Validees || 0}</p>
          <p className="text-[10px] text-green-500">Validées</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
          <p className="text-2xl font-bold text-red-600">{stats.Refusees || 0}</p>
          <p className="text-[10px] text-red-500">Refusées</p>
        </div>
      </div>

      {/* Compteur */}
      <div className="mb-3">
        <p className="text-xs text-gray-500">{demandes.length} demande(s) à traiter</p>
      </div>

      {/* Liste des demandes */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Chargement...</div>
        ) : currentItems.length === 0 ? (
          <div className="text-center py-8">
            <Clock size={40} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">Aucune demande en attente</p>
            <p className="text-xs text-gray-300 mt-1">Tout est à jour ! 🎉</p>
          </div>
        ) : (
          currentItems.map(d => (
            <div key={d.ID_DdeConges} className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">

              {/* En-tête carte */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User size={14} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{d.EmployePrenom} {d.EmployeNom}</p>
                    <p className="text-[10px] text-gray-400">{d.Matricule}</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-[10px] rounded-full bg-amber-100 text-amber-700 font-medium">N+{d.Niveau}</span>
              </div>

              {/* Détails */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <p className="text-sm font-semibold text-gray-700">{d.TypeCongeLibelle}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar size={12} className="text-gray-400" />
                  <p className="text-xs text-gray-500">{d.DateDebutFormatee} → {d.DateFinFormatee}</p>
                </div>
                <p className="text-xs text-gray-400 mt-1">{d.TotalDays} jour(s) ouvré(s)</p>
                {d.Commentaire && (
                  <p className="text-xs text-gray-400 mt-1 italic">"{d.Commentaire}"</p>
                )}
              </div>

              {/* Boutons */}
              <div className="flex gap-2">
                <button onClick={() => handleAction(d, 'Rejete')}
                  className="flex-1 px-3 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-medium transition flex items-center justify-center gap-1 active:scale-95">
                  <X size={16} /> Refuser
                </button>
                <button onClick={() => handleAction(d, 'Approuve')}
                  className="flex-1 px-3 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-1 active:scale-95">
                  <Check size={16} /> Approuver
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200">
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-gray-500">{currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Modale de validation */}
      <ValidationModal
        isOpen={showValidationModal}
        onClose={() => { setShowValidationModal(false); setSelectedDemande(null); }}
        onSuccess={handleValidationSuccess}
        demande={selectedDemande}
        actionType={actionType}
      />
    </div>
  );
}